import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { ChevronDownIcon, XMarkIcon } from "@heroicons/react/20/solid";

const Select = ({
  value,
  onChange,
  options = [],
  placeholder = "Select option",
  isDisabled = false,
  isRequired = false,
  isLoading = false,
  label,
  className = "",
  showLabel = true,
  isClearable = true,
  isSearchable = true,
  isMulti = false,
  getOptionValue,
  getOptionLabel,
  formatOptionLabel,
  noOptionsMessage = () => "No options available",
  loadingMessage = () => "Loading...",
  onInputChange,
  filterOption,
  openOnMount = false,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });

  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const optionRefs = useRef([]);
  const valueAreaRef = useRef(null);

  // Auto-open and focus search input on mount when requested (e.g. inline cell editors)
  useEffect(() => {
    if (!openOnMount) return;
    setIsOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resolveValue = useCallback(
    (option) => {
      if (option == null) return undefined;
      if (getOptionValue) return getOptionValue(option);
      if (typeof option === "string" || typeof option === "number") return option;
      return option?.value ?? option?.id ?? option;
    },
    [getOptionValue]
  );

  const resolveLabel = useCallback(
    (option) => {
      if (option == null) return "";
      if (getOptionLabel) return getOptionLabel(option);
      if (typeof option === "string" || typeof option === "number")
        return String(option);
      return option?.label ?? option?.name ?? option?.title ?? String(option);
    },
    [getOptionLabel]
  );

  const filteredOptions = useMemo(() => {
    if (filterOption === false || filterOption === null) return options;
    if (!search || !isSearchable) return options;
    const lower = search.toLowerCase();
    return options.filter((opt) => {
      if (typeof filterOption === "function") return filterOption(opt, search);
      const lbl = resolveLabel(opt);
      return lbl && String(lbl).toLowerCase().includes(lower);
    });
  }, [options, search, isSearchable, resolveLabel, filterOption]);

  const isOptionSelected = useCallback(
    (option) => {
      if (value == null) return false;
      const optVal = resolveValue(option);
      if (isMulti && Array.isArray(value)) {
        return value.some((v) => resolveValue(v) === optVal);
      }
      return resolveValue(value) === optVal;
    },
    [value, isMulti, resolveValue]
  );

  const handleSelect = useCallback(
    (option) => {
      if (isMulti) {
        const current = Array.isArray(value) ? value : [];
        const optVal = resolveValue(option);
        const exists = current.some((v) => resolveValue(v) === optVal);
        if (exists) {
          onChange(current.filter((v) => resolveValue(v) !== optVal));
        } else {
          onChange([...current, option]);
        }
        setSearch("");
        setTimeout(() => {
          inputRef.current?.focus();
          if (valueAreaRef.current) {
            valueAreaRef.current.scrollLeft = valueAreaRef.current.scrollWidth;
          }
        }, 0);
      } else {
        onChange(option);
        setIsOpen(false);
        setSearch("");
      }
    },
    [isMulti, value, onChange, resolveValue]
  );

  const handleClear = useCallback(
    (e) => {
      e.stopPropagation();
      onChange(isMulti ? [] : null);
      setSearch("");
    },
    [isMulti, onChange]
  );

  const handleRemoveMulti = useCallback(
    (e, opt) => {
      e.stopPropagation();
      if (!Array.isArray(value)) return;
      const optVal = resolveValue(opt);
      onChange(value.filter((v) => resolveValue(v) !== optVal));
    },
    [value, onChange, resolveValue]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (isDisabled) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setHighlightedIndex(0);
        } else {
          setHighlightedIndex((prev) =>
            prev < filteredOptions.length - 1 ? prev + 1 : 0
          );
        }
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        if (isOpen) {
          setHighlightedIndex((prev) =>
            prev > 0 ? prev - 1 : filteredOptions.length - 1
          );
        }
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (
          isOpen &&
          highlightedIndex >= 0 &&
          filteredOptions[highlightedIndex]
        ) {
          handleSelect(filteredOptions[highlightedIndex]);
        } else if (!isOpen) {
          setIsOpen(true);
        }
      } else if (e.key === "Escape") {
        setIsOpen(false);
        setSearch("");
      } else if (
        e.key === "Backspace" &&
        !search &&
        isMulti &&
        Array.isArray(value) &&
        value.length > 0
      ) {
        onChange(value.slice(0, -1));
      } else if (e.key === "Tab") {
        setIsOpen(false);
        setSearch("");
      }
    },
    [
      isDisabled,
      isOpen,
      highlightedIndex,
      filteredOptions,
      handleSelect,
      search,
      isMulti,
      value,
      onChange,
    ]
  );

  useEffect(() => {
    if (highlightedIndex >= 0 && optionRefs.current[highlightedIndex]) {
      optionRefs.current[highlightedIndex].scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndex]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target) &&
        menuRef.current &&
        !menuRef.current.contains(e.target)
      ) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [search]);

  const hasValue = isMulti
    ? Array.isArray(value) && value.length > 0
    : value != null;

  // Position the dropdown menu via portal
  useEffect(() => {
    if (!isOpen || !containerRef.current) return;

    const updatePosition = () => {
      const rect = containerRef.current.getBoundingClientRect();
      const menuEl = menuRef.current;
      const actualMenuHeight = menuEl ? menuEl.scrollHeight : 240;
      const maxMenuHeight = 240;
      const estimatedHeight = Math.min(actualMenuHeight, maxMenuHeight);
      const spaceBelow = window.innerHeight - rect.bottom;
      const openAbove = spaceBelow < estimatedHeight + 8 && rect.top > estimatedHeight + 8;

      setMenuPos({
        top: openAbove ? rect.top - estimatedHeight - 2 : rect.bottom + 2,
        left: rect.left,
        width: rect.width,
        openAbove,
      });
    };

    updatePosition();
    const rafId = requestAnimationFrame(updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, filteredOptions.length]);

  const showSearchInput = isOpen && isSearchable;
  const showSelectedText = !isMulti && hasValue && !showSearchInput;

  const renderMenu = () => {
    if (!isOpen) return null;

    const menu = (
      <div
        ref={menuRef}
        style={{
          position: "fixed",
          top: menuPos.top,
          left: menuPos.left,
          width: menuPos.width,
          zIndex: 9999,
          maxHeight: 240,
        }}
        className="bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 overflow-auto"
      >
        {isLoading ? (
          <div className="px-3 py-2 text-sm text-gray-500">
            {typeof loadingMessage === "function"
              ? loadingMessage()
              : loadingMessage}
          </div>
        ) : filteredOptions.length === 0 ? (
          <div className="px-3 py-2 text-sm text-gray-500">
            {typeof noOptionsMessage === "function"
              ? noOptionsMessage()
              : noOptionsMessage}
          </div>
        ) : (
          filteredOptions.map((option, idx) => {
            const selected = isOptionSelected(option);
            const highlighted = idx === highlightedIndex;

            return (
              <div
                key={resolveValue(option) ?? idx}
                ref={(el) => (optionRefs.current[idx] = el)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(option);
                }}
                onMouseEnter={() => setHighlightedIndex(idx)}
                className={`px-3 py-1.5 text-sm cursor-pointer select-none ${
                  selected
                    ? "bg-indigo-600 text-white"
                    : highlighted
                    ? "bg-indigo-50 text-gray-900"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                {formatOptionLabel
                  ? formatOptionLabel(option, { context: "menu" })
                  : resolveLabel(option)}
              </div>
            );
          })
        )}
      </div>
    );

    return createPortal(menu, document.body);
  };

  return (
    <div className={className}>
      {showLabel && label && (
        <label className="block text-sm font-medium leading-6 text-gray-900">
          {label} {isRequired && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className={showLabel && label ? "mt-2" : ""}>
        <div
          ref={containerRef}
          onClick={() => {
            if (isDisabled) return;
            setIsOpen(true);
            setTimeout(() => inputRef.current?.focus(), 0);
          }}
          onKeyDown={handleKeyDown}
          tabIndex={isSearchable ? -1 : 0}
          className={`relative flex w-full rounded-md border-0 py-1 px-3 ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:text-sm sm:leading-6 min-h-[36px] ${
            isDisabled
              ? "cursor-not-allowed bg-gray-200 text-gray-700 ring-gray-300"
              : "cursor-pointer bg-white text-gray-900"
          }`}
        >
          <div ref={valueAreaRef} className="flex flex-1 items-center gap-1 min-w-0 overflow-x-auto scrollbar-none">
            {/* Multi-value tags */}
            {isMulti &&
              Array.isArray(value) &&
              value.map((v) => (
                <span
                  key={resolveValue(v)}
                  className="inline-flex items-center gap-0.5 rounded bg-indigo-50 px-1.5 py-0.5 text-xs font-medium text-indigo-700 shrink-0 whitespace-nowrap"
                >
                  <span>
                    {formatOptionLabel
                      ? formatOptionLabel(v, { context: "value" })
                      : resolveLabel(v)}
                  </span>
                  {!isDisabled && (
                    <button
                      type="button"
                      onClick={(e) => handleRemoveMulti(e, v)}
                      className="ml-0.5 inline-flex items-center text-indigo-600 hover:text-indigo-800 shrink-0"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  )}
                </span>
              ))}

            {/* Selected value text (single mode, menu closed) */}
            {showSelectedText && (
              <span className="text-sm text-gray-900 truncate">
                {formatOptionLabel
                  ? formatOptionLabel(value, { context: "value" })
                  : resolveLabel(value)}
              </span>
            )}

            {/* Search input or placeholder */}
            {showSearchInput ? (
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => {
                  const val = e.target.value;
                  setSearch(val);
                  onInputChange?.(val);
                  if (!isOpen) setIsOpen(true);
                }}
                onKeyDown={handleKeyDown}
                placeholder={
                  hasValue && !isMulti ? resolveLabel(value) : placeholder
                }
                disabled={isDisabled}
                className="flex-1 min-w-[60px] outline-none border-0 p-0 text-sm bg-transparent placeholder:text-gray-400 focus:ring-0 disabled:cursor-not-allowed"
                autoComplete="off"
              />
            ) : !showSelectedText ? (
              isSearchable ? (
                <input
                  ref={inputRef}
                  type="text"
                  value=""
                  readOnly
                  placeholder={placeholder}
                  disabled={isDisabled}
                  className="flex-1 min-w-[60px] outline-none border-0 p-0 text-sm bg-transparent placeholder:text-gray-400 focus:ring-0 disabled:cursor-not-allowed cursor-pointer"
                  tabIndex={-1}
                />
              ) : (
                <span className="flex-1 text-sm text-gray-400 truncate">
                  {placeholder}
                </span>
              )
            ) : null}
          </div>

          {/* Action buttons - pinned to top-right, never wraps */}
          <div className="flex items-center gap-0.5 shrink-0 pl-1 self-start pt-0.5">
            {isLoading && (
              <svg
                className="animate-spin h-4 w-4 text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            {isClearable && hasValue && !isDisabled && (
              <button
                type="button"
                onClick={handleClear}
                className="p-0.5 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
            <ChevronDownIcon
              className={`h-4 w-4 text-gray-400 transition-transform duration-150 ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </div>
        </div>

        {renderMenu()}
      </div>
    </div>
  );
};

export default Select;
